/**
 * One copy of every repeated string in freshly parsed (or restored) data.
 *
 * `JSON.parse` allocates a new string for every occurrence, so an issue
 * response holds most article components' HTML twice (`text` and
 * `text_lang.en` carry the same markup) and the same placeholder image URL
 * once per component. Strings are immutable, so pointing every equal value at
 * one instance changes nothing for the code that reads them. In a four-issue
 * session this took the heap's strings from 18 MB to 8 MB together with the
 * `useIssue` closure fix (measured 2026-10-27).
 *
 * A walk after parsing, not a `JSON.parse` reviver: on a real 2.3 MB issue the
 * reviver cost ~29 ms on top of the 6 ms parse, the walk ~2 ms. It mutates the
 * object in place, so call it only on data nothing else has seen yet. The
 * table lives for one call only, so it never keeps old data alive. Short
 * strings are skipped (V8 already shares many, and the lookup would cost more
 * than it saves).
 */
const MIN_INTERN_LENGTH = 16;

export function internStrings<T>(root: T): T {
	if (!root || typeof root !== 'object') {
		return root;
	}
	const seen = new Map<string, string>();
	const intern = (value: string): string => {
		const existing = seen.get(value);
		if (existing !== undefined) {
			return existing;
		}
		seen.set(value, value);
		return value;
	};
	const stack: object[] = [root as object];
	while (stack.length) {
		const node = stack.pop() as Record<string, unknown> | unknown[];
		if (Array.isArray(node)) {
			for (let i = 0; i < node.length; i++) {
				const value = node[i];
				if (typeof value === 'string') {
					if (value.length >= MIN_INTERN_LENGTH) node[i] = intern(value);
				} else if (value && typeof value === 'object') {
					stack.push(value);
				}
			}
		} else {
			for (const key in node) {
				const value = node[key];
				if (typeof value === 'string') {
					if (value.length >= MIN_INTERN_LENGTH) node[key] = intern(value);
				} else if (value && typeof value === 'object') {
					stack.push(value);
				}
			}
		}
	}
	return root;
}

/** `JSON.parse` followed by `internStrings`, for `graphql-request`'s `jsonSerializer`. */
export function parseJsonInterned(text: string): unknown {
	return internStrings(JSON.parse(text));
}
